import Icon from './components/Icon';
// src/App.js
// CHANGES:
//   - /login  → PatientLoginPage  (phone + OTP)
//   - /nav-login → existing LoginPage (email + password, unchanged)
//   - PrivateRoute redirects to /nav-login when navigator session missing

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { AppShell } from './components/Layout';
import './index.css';

import LoginPage            from './pages/LoginPage';           // now at /nav-login
import PatientLoginPage     from './pages/PatientLoginPage';    // new, at /login
import DashboardPage        from './pages/DashboardPage';
import PatientListPage      from './pages/PatientListPage';
import PatientDetailPage    from './pages/PatientDetailPage';
import WalletLedgerPage     from './pages/WalletLedgerPage';
import PatientFormPage      from './pages/PatientFormPage';
import AppointmentsPage     from './pages/AppointmentsPage';
import BookAppointmentPage  from './pages/BookAppointmentPage';
import AddressPage          from './pages/AddressPage';
import DoctorsPage          from './pages/DoctorsPage';
import DoctorAvailabilityPage from './pages/DoctorAvailabilityPage';
import UsersPage            from './pages/UsersPage';
import ImportPatientsPage   from './pages/ImportPatientsPage';
import OrganizationsPage    from './pages/OrganizationsPage';
import OrganizationRosterPage from './pages/OrganizationRosterPage';
import InvitePage           from './pages/InvitePage';
import CtcScreeningListPage from './pages/CtcScreeningListPage';
import CtcScreeningFormPage from './pages/CtcScreeningFormPage';
import CtcScreeningDetailPage from './pages/CtcScreeningDetailPage';
import ImportScreeningsPage from './pages/ImportScreeningsPage';
import ProfilePage          from './pages/ProfilePage';
import RegisterPage         from './pages/RegisterPage';
import LabTestListPage      from './pages/LabTestListPage';
import LabTestFormPage      from './pages/LabTestFormPage';
import LabTestOrderDetailPage from './pages/LabTestOrderDetailPage';
import PreCallBriefingPage  from './pages/PreCallBriefingPage';
import TriageReportPage from './pages/TriageReportPage';
import DoctorHomePage from './pages/DoctorHomePage';
import ChatPage             from './pages/ChatPage';
import PatientTodayPage     from './pages/PatientTodayPage';

// ── Patient portal pages ──
import PatientAppointmentsPage  from './pages/PatientAppointmentsPage';
import PatientBookAppointmentPage from './pages/PatientBookAppointmentPage';
import PatientLabTestsPage      from './pages/PatientLabTestsPage';
import PatientWalletPage        from './pages/PatientWalletPage';
import PatientSubscriptionPage  from './pages/PatientSubscriptionPage';
import PatientInvoicesPage      from './pages/PatientInvoicesPage';
import PatientOrderLabTestPage  from './pages/PatientOrderLabTestPage';
import PatientSettingsPage      from './pages/PatientSettingsPage';
import PatientLabTestOrderDetailPage from './pages/PatientLabTestOrderDetailPage';
import { PatientProvider }      from './context/PatientContext';
import { NavigatorProvider }    from './context/NavigatorContext';

// ── Doctor portal pages ──
import DoctorPatientsPage       from './pages/DoctorPatientsPage';
import DoctorPatientDetailPage  from './pages/DoctorPatientDetailPage';
import DoctorAppointmentsPage   from './pages/DoctorAppointmentsPage';
import DoctorAppointmentDetailPage from './pages/DoctorAppointmentDetailPage';
import DoctorTriageReportsPage from './pages/DoctorTriageReportsPage';
import DoctorEscalationsPage from './pages/DoctorEscalationsPage';
import NavigatorEscalationsPage from './pages/NavigatorEscalationsPage';
import ScanQualityPage from './pages/ScanQualityPage';
import DoctorMyAvailabilityPage from './pages/DoctorMyAvailabilityPage';
import NavigatorMyAvailabilityPage from './pages/NavigatorMyAvailabilityPage';
import ClinicQueuePage from './pages/ClinicQueuePage';
import PatientPreviuAgePage    from './pages/PatientPreviuAgePage';
import PatientHealthTimeline   from './pages/PatientHealthTimeline';
import PatientOnboardingPage   from './pages/PatientOnboardingPage';
import PatientCarePlanPage     from './pages/PatientCarePlanPage';
import PatientVisitsPage       from './pages/PatientVisitsPage';
import PatientVisitSummaryPage from './pages/PatientVisitSummaryPage';
import PatientTriageResultPage from './pages/PatientTriageResultPage';
import PatientTriageReportPage from './pages/PatientTriageReportPage';
import DoctorTriageReportPage from './pages/DoctorTriageReportPage';
import PatientCurrentMarkersPage from './pages/PatientCurrentMarkersPage';
import PatientLifestylePage from './pages/PatientLifestylePage';
import PatientUploadPage       from './pages/PatientUploadPage';
import PatientRecordsPage      from './pages/PatientRecordsPage';
import PatientCareTeamPage     from './pages/PatientCareTeamPage';
import StaffConversationPage   from './pages/StaffConversationPage';
import TeamInboxPage           from './pages/TeamInboxPage';
import PatientMessagesInboxPage from './pages/PatientMessagesInboxPage';

// ── Guardian Dubai — subscriber portal pages ──
import SubscriberLoginPage      from './pages/SubscriberLoginPage';
import SubscriberDashboardPage  from './pages/SubscriberDashboardPage';
import SubscriberPlanPage       from './pages/SubscriberPlanPage';
import SubscriberAddBeneficiaryPage    from './pages/SubscriberAddBeneficiaryPage';
import SubscriberBeneficiaryDetailPage from './pages/SubscriberBeneficiaryDetailPage';
import SubscriberChatPage       from './pages/SubscriberChatPage';
import SubscriberCheckoutPage   from './pages/SubscriberCheckoutPage';
import { SubscriberProvider }   from './context/SubscriberContext';
import ErrorBoundary            from './components/ErrorBoundary';
import InstallPrompt            from './components/InstallPrompt';
import InstallPage              from './pages/InstallPage';


// PrivateRoute guards navigator/doctor pages.
// Redirects to /nav-login (NOT /login) on missing session.
//
// allowedRoles is optional — most routes just need "some staff session
// exists" and leave it unset. A handful (SEC-S16: /organizations,
// /patients/import, /users) are ADMIN/HEALTH_NAVIGATOR-only on the backend
// (SecurityConfig's requestMatchers for /api/organizations/**,
// /api/patients/import, /api/navigators/**) but previously rendered for ANY
// authenticated staff role here — a DOCTOR or SUPPORT_STAFF account could
// navigate straight to the page shell even though every data call it made
// would then 403. The backend was always the real enforcement; this closes
// the client-side defense-in-depth gap on top of it.
function PrivateRoute({ children, allowedRoles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/nav-login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

// PatientRoute guards patient portal pages.
// Redirects to /login if previu_patient not in localStorage.
function PatientRoute({ children }) {
  const patient = (() => {
    try { return JSON.parse(localStorage.getItem('previu_patient')); }
    catch { return null; }
  })();
  return patient ? children : <Navigate to="/login" replace />;
}

// SubscriberRoute guards Guardian Dubai subscriber portal pages.
// Redirects to /subscriber-login if previu_subscriber not in localStorage —
// same shape as PatientRoute above, for the third principal type.
function SubscriberRoute({ children }) {
  const subscriber = (() => {
    try { return JSON.parse(localStorage.getItem('previu_subscriber')); }
    catch { return null; }
  })();
  return subscriber ? children : <Navigate to="/subscriber-login" replace />;
}

// Root ("/") redirect. An anonymous visitor goes to /login — the patient
// login, the consumer-facing default for the bare domain. Previously this
// route only checked the navigator/doctor session and sent everyone else to
// /dashboard, which PrivateRoute then bounced to /nav-login (STAFF login) —
// so hitting the bare domain logged out landed on staff login, not patient
// login.
//
// An already-logged-in patient or subscriber hitting "/" still lands on
// their own portal rather than a login screen they've already passed — same
// "fail towards the portal" reasoning resolvePostLoginRoute (PatientLoginPage)
// uses for its own fallback.
function RootRedirect() {
  const { user } = useAuth();
  if (user) {
    return <Navigate to={user.role === 'DOCTOR' ? '/doctor/home' : '/dashboard'} replace />;
  }
  try {
    // Synchronous, off a thin localStorage cache; patients always land on Today.
    if (localStorage.getItem('previu_patient')) return <Navigate to="/patient/today" replace />;
  } catch { /* localStorage unavailable — fall through to /login */ }
  try {
    if (localStorage.getItem('previu_subscriber')) return <Navigate to="/subscriber/dashboard" replace />;
  } catch { /* localStorage unavailable — fall through to /login */ }
  return <Navigate to="/login" replace />;
}

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* ── Patient login (phone + OTP) ── */}
      <Route path="/login" element={<PatientLoginPage />} />

      {/* ── Navigator / Doctor login (email + password) ── */}
      <Route
        path="/nav-login"
        element={
          user
            ? user.role === 'DOCTOR'
              ? <Navigate to="/doctor/home" replace />
              : <Navigate to="/dashboard" replace />
            : <LoginPage />
        }
      />

      {/* ── Root redirect — see RootRedirect above ── */}
      <Route path="/" element={<RootRedirect />} />

      {/* ── Navigator / Doctor protected routes ── */}
      {/* ErrorBoundary added Aug 2026: a render error on these two landing
          routes had nothing to catch it, so the whole tree unmounted and the
          user got a silent blank page with no clue what happened — which is
          exactly how the AppShell hooks bug presented. An uncaught error here
          should show a message, not nothing. */}
      <Route path="/dashboard" element={
        <PrivateRoute>
          <ErrorBoundary label="Dashboard">
            {user?.role === 'DOCTOR'
              ? <Navigate to="/doctor/home" replace />
              : <DashboardPage />}
          </ErrorBoundary>
        </PrivateRoute>
      } />
      <Route path="/quality-check"
        element={<PrivateRoute><ErrorBoundary label="Quality check"><ScanQualityPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/quality-check/:recordId"
        element={<PrivateRoute><ErrorBoundary label="Quality check"><ScanQualityPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/escalations"
        element={<PrivateRoute><ErrorBoundary label="Escalations"><NavigatorEscalationsPage /></ErrorBoundary></PrivateRoute>} />
      {/* Front-desk queue — navigator/admin/support (the nav item is hidden for doctors). */}
      <Route path="/clinic/queue"
        element={<PrivateRoute allowedRoles={['ADMIN', 'HEALTH_NAVIGATOR']}><ErrorBoundary label="Today's clinic"><ClinicQueuePage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/patients"  element={<PrivateRoute><PatientListPage /></PrivateRoute>} />
      <Route path="/patients/new" element={<PrivateRoute><PatientFormPage /></PrivateRoute>} />
      <Route path="/patients/:id" element={<PrivateRoute><PatientDetailPage /></PrivateRoute>} />
      <Route path="/patients/:patientId/wallet" element={<PrivateRoute><WalletLedgerPage /></PrivateRoute>} />
      <Route path="/patients/:patientId/chat" element={<PrivateRoute><StaffConversationPage conversationType="PATIENT_NAVIGATOR" /></PrivateRoute>} />
      <Route path="/team-chat" element={<PrivateRoute><TeamInboxPage /></PrivateRoute>} />
      <Route path="/patient-messages" element={<PrivateRoute><PatientMessagesInboxPage /></PrivateRoute>} />
      <Route path="/patients/:id/reports/:recordId" element={<PrivateRoute><ErrorBoundary label="Health report"><TriageReportPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/patients/:id/pre-call-briefing" element={<PrivateRoute><PreCallBriefingPage /></PrivateRoute>} />
      <Route path="/patients/:id/edit" element={<PrivateRoute><PatientFormPage /></PrivateRoute>} />
      <Route path="/patients/:id/addresses" element={<PrivateRoute><AddressPage /></PrivateRoute>} />
      <Route path="/patients/import" element={<PrivateRoute allowedRoles={['ADMIN', 'HEALTH_NAVIGATOR']}><ImportPatientsPage /></PrivateRoute>} />
      {/* Corporate onboarding — staff manage rosters; the invitee's page is public. */}
      <Route path="/organizations" element={<PrivateRoute allowedRoles={['ADMIN', 'HEALTH_NAVIGATOR']}><OrganizationsPage /></PrivateRoute>} />
      <Route path="/organizations/:id" element={<PrivateRoute allowedRoles={['ADMIN', 'HEALTH_NAVIGATOR']}><OrganizationRosterPage /></PrivateRoute>} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/ctc-screenings" element={<PrivateRoute><CtcScreeningListPage /></PrivateRoute>} />
      <Route path="/ctc-screenings/import" element={<PrivateRoute><ImportScreeningsPage /></PrivateRoute>} />
      <Route path="/ctc-screenings/new" element={<PrivateRoute><CtcScreeningFormPage /></PrivateRoute>} />
      <Route path="/ctc-screenings/:id" element={<PrivateRoute><CtcScreeningDetailPage /></PrivateRoute>} />
      <Route path="/ctc-screenings/:id/edit" element={<PrivateRoute><CtcScreeningFormPage /></PrivateRoute>} />
      <Route path="/appointments" element={<PrivateRoute><AppointmentsPage /></PrivateRoute>} />
      <Route path="/appointments/new" element={<PrivateRoute><BookAppointmentPage /></PrivateRoute>} />
      {/* /lab-triage route hidden (Sep 2026) — LabTriagePage/LabTriageEngine
          never persist a TriageResult and can't run Model 3, so there's no
          honest way to offer a Claude/Deterministic/Both selector there.
          Code untouched at src/pages/LabTriagePage.js; re-add the import
          above and this route to restore it. */}
      <Route path="/lab-tests" element={<PrivateRoute><LabTestListPage /></PrivateRoute>} />
      <Route path="/lab-tests/new" element={<PrivateRoute><LabTestFormPage /></PrivateRoute>} />
      <Route path="/lab-tests/orders/:orderId" element={<PrivateRoute><LabTestOrderDetailPage /></PrivateRoute>} />
      <Route path="/records"  element={<PrivateRoute><Placeholder title="Health Records" /></PrivateRoute>} />
      <Route path="/doctors"  element={<PrivateRoute><DoctorsPage /></PrivateRoute>} />
      <Route path="/doctors/:id/availability" element={<PrivateRoute><DoctorAvailabilityPage /></PrivateRoute>} />
      <Route path="/settings" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
      {/* UsersPage calls GET /api/navigators, which NavigatorController.
          getAllNavigators() restricts to ADMIN only (@PreAuthorize) — narrower
          than the other three SEC-S16 routes, which are ADMIN+HEALTH_NAVIGATOR. */}
      <Route path="/users"    element={<PrivateRoute allowedRoles={['ADMIN']}><UsersPage /></PrivateRoute>} />
      <Route path="/profile"  element={<PrivateRoute><ProfilePage /></PrivateRoute>} />

      {/* ── Doctor portal routes ── */}
      <Route path="/doctor/home"
        element={<PrivateRoute><ErrorBoundary label="Home"><DoctorHomePage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/doctor/patients"
        element={<PrivateRoute><ErrorBoundary label="My Patients"><DoctorPatientsPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/doctor/patients/:patientId"
        element={<PrivateRoute><DoctorPatientDetailPage /></PrivateRoute>} />
      <Route path="/doctor/patients/:patientId/reports/:reportId"
        element={<PrivateRoute><ErrorBoundary label="Patient view"><DoctorTriageReportPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/doctor/patients/:patientId/chat"
        element={<PrivateRoute><StaffConversationPage conversationType="PATIENT_DOCTOR" /></PrivateRoute>} />
      <Route path="/doctor/team-chat"
        element={<PrivateRoute><TeamInboxPage /></PrivateRoute>} />
      <Route path="/doctor/patient-messages"
        element={<PrivateRoute><PatientMessagesInboxPage /></PrivateRoute>} />
      <Route path="/doctor/appointments"
        element={<PrivateRoute><DoctorAppointmentsPage /></PrivateRoute>} />
      <Route path="/doctor/appointments/:appointmentId"
        element={<PrivateRoute><DoctorAppointmentDetailPage /></PrivateRoute>} />
      <Route path="/doctor/triage-reports"
        element={<PrivateRoute><DoctorTriageReportsPage /></PrivateRoute>} />
      <Route path="/doctor/escalations"
        element={<PrivateRoute><ErrorBoundary label="Escalations"><DoctorEscalationsPage /></ErrorBoundary></PrivateRoute>} />
      <Route path="/doctor/availability"
        element={<PrivateRoute><DoctorMyAvailabilityPage /></PrivateRoute>} />
      <Route path="/navigator/availability"
        element={<PrivateRoute><NavigatorMyAvailabilityPage /></PrivateRoute>} />

      {/* ── Patient self-registration (public) ──
          /welcome and /book used to sit here as the WhatsApp entry flow:
          a WATI message linked to /welcome?token=..., which offered
          "Book an appointment" -> /book?token=..., which fetched /slots
          and POSTed to /webhook/confirm-booking. Booking from WhatsApp
          directly was abandoned; the backend endpoints that flow depended
          on are gone, so both pages are removed rather than left to 404.
          Patients book through /patient/appointments/book now. */}
      <Route path="/register" element={<RegisterPage />} />

      {/* Public install guide + QR code — reachable without signing in. */}
      <Route path="/install" element={<InstallPage />} />

      {/* ── Patient chat (direct login entry) ── */}
      <Route path="/chat" element={<PatientRoute><ChatPage /></PatientRoute>} />

      {/* ── Patient portal routes ── */}
      {/* Light assistant mode's default landing screen post-login (see
          PatientLoginPage.resolvePostLoginRoute) — Ask Previu
          (/patient/chat) stays reachable from the sidebar, just without the
          cards this screen already shows. */}
      <Route path="/patient/today"
        element={<PatientRoute><PatientTodayPage /></PatientRoute>} />
      <Route path="/patient/chat"
        element={<PatientRoute><ChatPage /></PatientRoute>} />
      <Route path="/patient/appointments"
        element={<PatientRoute><PatientAppointmentsPage /></PatientRoute>} />
      <Route path="/patient/appointments/navigator"
        element={<PatientRoute><PatientAppointmentsPage only="navigator" /></PatientRoute>} />
      <Route path="/patient/appointments/book"
        element={<PatientRoute><PatientBookAppointmentPage /></PatientRoute>} />
      <Route path="/patient/lab-tests"
        element={<PatientRoute><PatientLabTestsPage /></PatientRoute>} />
      <Route path="/patient/lab-tests/:orderId"
        element={<PatientRoute><PatientLabTestOrderDetailPage /></PatientRoute>} />
      <Route path="/patient/lab-tests/order"
        element={<PatientRoute><PatientOrderLabTestPage /></PatientRoute>} />
      <Route path="/patient/wallet"
        element={<PatientRoute><PatientWalletPage /></PatientRoute>} />
      <Route path="/patient/subscription"
        element={<PatientRoute><PatientSubscriptionPage /></PatientRoute>} />
      <Route path="/patient/invoices"
        element={<PatientRoute><PatientInvoicesPage /></PatientRoute>} />
      <Route path="/patient/settings"
        element={<PatientRoute><PatientSettingsPage /></PatientRoute>} />

      {/* ── Patient Health Timeline ── */}
      <Route path="/patient/timeline"
        element={<PatientRoute><PatientHealthTimeline /></PatientRoute>} />

      {/* ── Patient Previu Age ── */}
      <Route path="/patient/previu-age"
        element={<PatientRoute><PatientPreviuAgePage /></PatientRoute>} />

      {/* ── Sprint 12: Onboarding, Care Plan, Triage Results ── */}
      <Route path="/patient/onboarding"
        element={<PatientRoute><PatientOnboardingPage /></PatientRoute>} />
      <Route path="/patient/care-plan"
        element={<PatientRoute><PatientCarePlanPage /></PatientRoute>} />
      <Route path="/patient/results/:triageId/report"
        element={<PatientRoute><PatientTriageReportPage /></PatientRoute>} />
      <Route path="/patient/results/:triageId?"
        element={<PatientRoute><PatientTriageResultPage /></PatientRoute>} />
      <Route path="/patient/markers"
        element={<PatientRoute><PatientCurrentMarkersPage /></PatientRoute>} />
      <Route path="/patient/lifestyle"
        element={<PatientRoute><PatientLifestylePage /></PatientRoute>} />
      <Route path="/patient/upload"
        element={<PatientRoute><PatientUploadPage /></PatientRoute>} />
      <Route path="/patient/records"
        element={<PatientRoute><PatientRecordsPage /></PatientRoute>} />
      <Route path="/patient/care-team"
        element={<PatientRoute><PatientCareTeamPage /></PatientRoute>} />
      <Route path="/patient/visits"
        element={<PatientRoute><PatientVisitsPage /></PatientRoute>} />
      <Route path="/patient/visits/:visitId"
        element={<PatientRoute><PatientVisitSummaryPage /></PatientRoute>} />

      {/* ── Guardian Dubai — subscriber portal routes ── */}
      {/* Wrapped in ErrorBoundary (30 Jul 2026) — a blank white screen with
          no navy topbar/sidebar at all is the signature of an uncaught
          render error with nothing catching it; these routes previously had
          no safety net. */}
      <Route path="/subscriber-login"    element={<SubscriberLoginPage />} />
      <Route path="/subscriber-register" element={<SubscriberLoginPage />} />
      <Route path="/subscriber/dashboard" element={
        <SubscriberRoute><ErrorBoundary label="Family dashboard"><SubscriberDashboardPage /></ErrorBoundary></SubscriberRoute>
      } />
      <Route path="/subscriber/plan" element={
        <SubscriberRoute><ErrorBoundary label="Plan selection"><SubscriberPlanPage /></ErrorBoundary></SubscriberRoute>
      } />
      <Route path="/subscriber/add-beneficiary" element={
        <SubscriberRoute><ErrorBoundary label="Add family member"><SubscriberAddBeneficiaryPage /></ErrorBoundary></SubscriberRoute>
      } />
      <Route path="/subscriber/beneficiaries/:patientId" element={
        <SubscriberRoute><ErrorBoundary label="Family member detail"><SubscriberBeneficiaryDetailPage /></ErrorBoundary></SubscriberRoute>
      } />
      <Route path="/subscriber/chat" element={
        <SubscriberRoute><ErrorBoundary label="Chat"><SubscriberChatPage /></ErrorBoundary></SubscriberRoute>
      } />
      <Route path="/subscriber/checkout" element={
        <SubscriberRoute><ErrorBoundary label="Checkout"><SubscriberCheckoutPage /></ErrorBoundary></SubscriberRoute>
      } />

      {/* ── Fallback ── */}
      <Route path="*" element={
        user?.role === 'DOCTOR'
          ? <Navigate to="/doctor/home" replace />
          : <Navigate to="/dashboard" replace />
      } />
    </Routes>
  );
}

function Placeholder({ title }) {
  return (
    <AppShell breadcrumbs={[{ label: title }]}>
      <div className="empty-state" style={{ paddingTop: 80 }}>
        <div className="empty-icon empty-icon--badge" aria-hidden="true"><Icon name="alert" size={20} /></div>
        <div className="empty-title">{title}</div>
        <div className="empty-desc">This section is coming soon</div>
      </div>
    </AppShell>
  );
}

// Google OAuth client IDs are public by design — this one is already committed
// in .env.example and travels in the URL of every OAuth request. It is defaulted
// here, the same way every API base URL in this app defaults to the Railway host,
// because an ABSENT value is fatal rather than degrading: GoogleOAuthProvider
// throws "Missing required parameter client_id" on an empty string, and since it
// wraps the whole tree that throw takes down every route — including /nav-login,
// which has no Google button at all.
//
// 28 Aug 2026: that is exactly what happened. The variable was unset in the
// build environment, `|| ''` did what it was written to do, and the entire app
// rendered the ErrorBoundary card.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID
  || '944005454528-hva47ggqjmmoem0tg4j257clepjdlrtl.apps.googleusercontent.com';

const CLIENT_CONFIG_URL = `${process.env.REACT_APP_API_URL
  || 'https://previuagenticplatform-production.up.railway.app/api'}/public/client-config`;

/**
 * The client id, with the backend allowed to OVERRIDE the constant above —
 * never to replace it.
 *
 * The constant alone means pointing an environment at a different OAuth client
 * requires editing and rebuilding the app: the same duplication that had the
 * Razorpay key living in two places. GET /api/public/client-config serves it
 * from Railway, so an environment can differ without a rebuild.
 *
 * But it starts from the constant and only ever upgrades, because the failure
 * modes are not symmetrical. An absent client id is FATAL rather than
 * degrading — GoogleOAuthProvider throws "Missing required parameter client_id"
 * on an empty string, and it wraps the whole route tree, so the app renders the
 * ErrorBoundary card and nothing works, including /nav-login which has no
 * Google button at all. That happened on 28 Aug 2026. A fetch can fail; a
 * constant cannot. So a failed or empty response is ignored entirely and the
 * constant stands.
 *
 * Setting the same value React already holds is a no-op — React bails out of
 * the re-render on an identical string — so in the normal case where backend
 * and constant agree, the provider is never re-initialised. It re-initialises
 * only when they genuinely differ, which is exactly when it should.
 */
function useGoogleClientId() {
  const [clientId, setClientId] = React.useState(GOOGLE_CLIENT_ID);

  React.useEffect(() => {
    let cancelled = false;
    fetch(CLIENT_CONFIG_URL)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        const fromBackend = (data?.googleClientId || '').trim();
        if (!cancelled && fromBackend) setClientId(fromBackend);
      })
      .catch(() => { /* keep the constant — see above */ });
    return () => { cancelled = true; };
  }, []);

  return clientId;
}

export default function App() {
  const googleClientId = useGoogleClientId();

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <AuthProvider>
          <NavigatorProvider>
            <ToastProvider>
              <PatientProvider>
                <SubscriberProvider>
                  {/* Root boundary. Individual routes below have their own, but
                      only some of them do, and nothing at all guarded AppRoutes,
                      the providers, or PrivateRoute — a throw in any of those
                      unmounted the entire tree and rendered literal nothing.
                      That is what a "blank screen" report has meant every time:
                      not a styling problem, an uncaught error with no floor under
                      it. The inner boundaries stay because they fail smaller —
                      one section breaks, the surrounding page survives. This one
                      only ever catches what got past them. */}
                  <ErrorBoundary label="Previu" root>
                    <AppRoutes />
                  </ErrorBoundary>
                  <InstallPrompt />
                </SubscriberProvider>
              </PatientProvider>
            </ToastProvider>
          </NavigatorProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}


